import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DialogContent } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import "./ConfirmBox.scss";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Every "Are you sure?" box of the site (48 screens): the same look as the site's popups (UiComponents/Scss/_swal.scss) —
// a white card, a ring icon in the colour of the action, the question in navy, and the action button coloured by what
// it does. The props are unchanged; `img` is no longer drawn (the icon follows the action instead).
const NAVY = "#1E3A5F";
const NAVY_LIGHT = "#2E5A8F";
const RED = "#F04438";
const RED_DARK = "#D92D20";
const GREEN = "#12B76A";

const DANGER_RE = /delete|remove|reject|inactive|disable|deactivat|block|failure|cancel/i;
const POSITIVE_RE = /accept|approve|active|enable|success|confirm/i;

/**
 * What a confirm box is about, from its own words (title / button): "danger" (Delete, Reject, Inactive, Disable,
 * Confirm Failure ...: a red button), "positive" (Accept, Active, Confirm Success: green icon, navy button), "logout",
 * or "neutral" (navy).
 */
export const confirmTone = ({ title, confirmButton } = {}) => {
  const words = `${title || ""} ${confirmButton || ""}`;
  if (DANGER_RE.test(words)) return "danger";
  if (/logout|log out|sign out/i.test(words)) return "logout";
  if (POSITIVE_RE.test(words)) return "positive";
  return "neutral";
};

const TONES = {
  danger: { accent: RED, button: RED, hover: RED_DARK },
  positive: { accent: GREEN, button: NAVY, hover: NAVY_LIGHT },
  logout: { accent: NAVY, button: NAVY, hover: NAVY_LIGHT },
  neutral: { accent: NAVY, button: NAVY, hover: NAVY_LIGHT },
};

const toneIcon = (tone, words) => {
  const sx = { fontSize: 38 };
  if (tone === "danger") return /delete|remove/i.test(words) ? <DeleteOutlineRoundedIcon sx={sx} /> : <BlockRoundedIcon sx={sx} />;
  if (tone === "positive") return <CheckRoundedIcon sx={sx} />;
  if (tone === "logout") return <LogoutRoundedIcon sx={sx} />;
  return <HelpOutlineRoundedIcon sx={sx} />;
};

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      style={{
        transformOrigin: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
      ref={ref}
      {...props}
    />
  );
});

const buttonBase = {
  textTransform: "none",
  fontFamily: "Poppins, sans-serif",
  fontSize: "13.5px",
  fontWeight: 500,
  borderRadius: "8px",
  minWidth: 120,
  padding: "9px 22px",
  boxShadow: "none",
};

export default function ConfirmBox({
  open,
  title,
  content,
  subContent,
  confirmButton,
  // opt-in: the dismiss button's words (default "Cancel" - e.g. "Stay on the form" when the action itself is a Cancel)
  cancelButton = "Cancel",
  setOpenConfirmBox,
  Function,
  mode,
  onClose,
  initialDate,
}) {
  const handleClose = () => {
    setOpenConfirmBox(false);
    if (onClose) onClose();
  };

  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (open) {
      // initialDate is optional: callers that don't pass it get today
      const start = initialDate ? dayjs(initialDate) : null;
      setSelectedDate(start && start.isValid() ? start : dayjs());
    }
  }, [open]);

  const handleDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      setSelectedDate(newValue);
    }
  };

  const tone = confirmTone({ title, confirmButton });
  const colors = TONES[tone];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      className="DialogBox"
      TransitionComponent={FadeTransition}
      PaperProps={{ className: "ConfirmBoxPaper", "data-tone": tone }}
      BackdropProps={{ style: { backdropFilter: "blur(2px)", backgroundColor: "rgba(16, 24, 40, 0.55)" } }}
    >
      <DialogContent sx={{ padding: "30px 28px 8px", textAlign: "center" }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 72,
            height: 72,
            boxSizing: "border-box",
            borderRadius: "50%",
            border: `4px solid ${colors.accent}40`,
            color: colors.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
          }}
        >
          {toneIcon(tone, `${title || ""} ${confirmButton || ""}`)}
        </Box>

        <Typography
          className="delete_warning"
          sx={{
            "&::after": {
              content: '""',
              display: "block",
              width: 36,
              height: 3,
              margin: "12px auto 0",
              borderRadius: "3px",
              backgroundColor: colors.accent,
            },
          }}
        >
          {content}
        </Typography>
        {subContent ? <Typography className="delete_warning_subtext">{subContent}</Typography> : null}

        {mode ? (
          <Stack direction="row" spacing={2} sx={{ mt: 2.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker className="Date_Picker w-100" value={selectedDate} onChange={handleDateChange} format="DD/MM/YYYY" />
            </LocalizationProvider>
          </Stack>
        ) : null}
      </DialogContent>

      <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ padding: "20px 24px 24px" }}>
        <Button
          // a red action starts on Cancel, so Enter never deletes by accident
          autoFocus={tone === "danger"}
          onClick={handleClose}
          sx={{
            ...buttonBase,
            border: "1px solid #D0D5DD",
            background: "#fff",
            color: "#344054",
            "&:hover": { background: "#F9FAFB" },
            "&:focus-visible": { boxShadow: "0 0 0 3px rgba(152, 162, 179, 0.28)" },
          }}
        >
          {cancelButton}
        </Button>
        <Button
          onClick={() => {
            Function(selectedDate);
            handleClose();
          }}
          sx={{
            ...buttonBase,
            background: colors.button,
            color: "#fff",
            "&:hover": { background: colors.hover, boxShadow: "none" },
            "&:focus-visible": { boxShadow: `0 0 0 3px ${colors.button}47` },
          }}
        >
          {confirmButton}
        </Button>
      </Stack>
    </Dialog>
  );
}
